import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  Camera,
  CameraOff,
  User,
  Phone,
  Package,
  CheckCircle2,
  XCircle,
  X,
  Search,
  DollarSign,
} from "lucide-react";
import { adminApi, isAdminLoggedIn, verifyAdminToken } from "@/utils/auth";

export default function ScannerPage() {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scannedRegistrant, setScannedRegistrant] = useState(null);
  const [manualSearch, setManualSearch] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [searching, setSearching] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
      }
    };
    checkAuth();

    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error("Camera not supported on this device");
        return;
      }

      // Request camera permission first (important for iOS)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream
      } catch (permErr) {
        toast.error("Please allow camera access in your browser settings");
        console.error("Camera permission error:", permErr);
        return;
      }

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        onScanError
      );
      setScanning(true);
      toast.success("Scanner started - point at QR code");
    } catch (err) {
      console.error("Scanner error:", err);
      if (err.name === "NotAllowedError") {
        toast.error("Camera access denied. Please allow camera in Settings > Safari > Camera");
      } else if (err.name === "NotFoundError") {
        toast.error("No camera found on this device");
      } else {
        toast.error("Could not start camera: " + err.message);
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    await lookupRegistrant(decodedText);
  };

  const onScanError = (error) => {
    // Ignore scan errors (no QR code in frame)
  };

  const lookupRegistrant = async (idOrRegNumber) => {
    try {
      // Try by ID first
      const response = await adminApi.get(`/registrants/${idOrRegNumber}`);
      setScannedRegistrant(response.data);
      setAmountPaid(response.data.amount_paid?.toString() || "");
      toast.success("Registrant found!");
    } catch (error) {
      // Try by registration number
      try {
        const regNum = parseInt(idOrRegNumber.replace("#", "").replace(/^0+/, ""));
        if (!isNaN(regNum)) {
          const response = await adminApi.get(`/registrants/lookup/${regNum}`);
          setScannedRegistrant(response.data);
          setAmountPaid(response.data.amount_paid?.toString() || "");
          toast.success("Registrant found!");
          return;
        }
      } catch (e) {
        // Fall through to error
      }
      if (error.response?.status !== 401) {
        toast.error("Registrant not found");
      }
      if (scanning) {
        setTimeout(() => startScanner(), 1000);
      }
    }
  };

  const handleManualSearch = async (e) => {
    e.preventDefault();
    if (!manualSearch.trim()) return;
    
    setSearching(true);
    await lookupRegistrant(manualSearch.trim());
    setSearching(false);
  };

  const markCollected = async () => {
    if (!scannedRegistrant) return;

    try {
      const response = await adminApi.patch(`/registrants/${scannedRegistrant.id}/collect`);
      setScannedRegistrant(response.data);
      toast.success("Package marked as collected!");
    } catch (error) {
      toast.error("Failed to mark as collected");
    }
  };

  const updatePayment = async (newStatus) => {
    if (!scannedRegistrant) return;

    try {
      const response = await adminApi.patch(`/registrants/${scannedRegistrant.id}/payment`, { 
        payment_status: newStatus,
        amount_paid: parseFloat(amountPaid) || 0
      });
      setScannedRegistrant(response.data);
      toast.success(`Payment updated - $${amountPaid || 0} ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const closeResult = () => {
    setScannedRegistrant(null);
    setAmountPaid("");
    setManualSearch("");
  };

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white" data-testid="scanner-title">
              QR SCANNER
            </h1>
            <p className="text-muted-foreground">Scan or search to collect packages</p>
          </div>
        </div>

        {/* Manual Search */}
        <Card className="glass border-white/10 mb-4">
          <CardContent className="p-4">
            <form onSubmit={handleManualSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="manual-search"
                  placeholder="Enter registration # (e.g. 001 or #001)"
                  value={manualSearch}
                  onChange={(e) => setManualSearch(e.target.value)}
                  className="pl-10 bg-muted/50 border-transparent h-12"
                />
              </div>
              <Button 
                type="submit" 
                disabled={searching}
                className="bg-primary hover:bg-primary/90 text-black h-12 px-6"
                data-testid="search-btn"
              >
                {searching ? "..." : "Find"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Scanner Area */}
        {!scannedRegistrant && (
          <Card className="glass border-white/10 mb-6 overflow-hidden">
            <CardContent className="p-0">
              <div
                id="qr-reader"
                ref={scannerRef}
                className="w-full aspect-square bg-black/50"
                style={{ display: scanning ? "block" : "none" }}
              />
              
              {!scanning && (
                <div className="w-full aspect-square flex flex-col items-center justify-center bg-muted/20">
                  <Camera className="w-16 h-16 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-6 text-center px-4">
                    Camera is off. Start scanning or use manual search above.
                  </p>
                  <Button
                    onClick={startScanner}
                    className="bg-primary hover:bg-primary/90 text-black"
                    data-testid="start-scanner-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Start Scanner
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scanner Controls */}
        {scanning && !scannedRegistrant && (
          <div className="flex justify-center mb-6">
            <Button
              onClick={stopScanner}
              variant="outline"
              className="border-white/10"
              data-testid="stop-scanner-btn"
            >
              <CameraOff className="w-5 h-5 mr-2" />
              Stop Scanner
            </Button>
          </div>
        )}

        {/* Scanned Result */}
        <AnimatePresence>
          {scannedRegistrant && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="glass border-primary/50 glow-cyan" data-testid="scanned-result">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Registration #
                    </p>
                    <CardTitle className="text-4xl font-black text-primary neon-cyan" data-testid="scanned-reg-number">
                      #{String(scannedRegistrant.reg_number).padStart(3, "0")}
                    </CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeResult}
                    className="text-muted-foreground hover:text-white"
                    data-testid="close-result-btn"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Registrant Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <User className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-white font-medium" data-testid="scanned-name">
                          {scannedRegistrant.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Phone className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Contact</p>
                        <p className="text-white font-medium" data-testid="scanned-contact">
                          {scannedRegistrant.contact}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Package className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Package</p>
                        <p className="text-white font-medium" data-testid="scanned-package">
                          {scannedRegistrant.package}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Package className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Size</p>
                        <p className="text-white font-medium" data-testid="scanned-size">
                          {scannedRegistrant.size}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex gap-3 justify-center">
                    <Badge
                      className={`${
                        scannedRegistrant.payment_status === "paid"
                          ? "status-paid"
                          : "status-unpaid"
                      } px-4 py-2 text-sm uppercase`}
                      data-testid="scanned-payment-status"
                    >
                      {scannedRegistrant.payment_status === "paid" ? (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      {scannedRegistrant.payment_status}
                      {scannedRegistrant.amount_paid > 0 && ` - $${scannedRegistrant.amount_paid}`}
                    </Badge>
                    <Badge
                      className={`${
                        scannedRegistrant.collected
                          ? "status-collected"
                          : "bg-muted text-muted-foreground"
                      } px-4 py-2 text-sm uppercase`}
                      data-testid="scanned-collected-status"
                    >
                      {scannedRegistrant.collected ? "Collected" : "Not Collected"}
                    </Badge>
                  </div>

                  {/* Payment Amount Input */}
                  <div className="space-y-2">
                    <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                      Amount Paid ($)
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        data-testid="amount-paid-input"
                        type="number"
                        placeholder="0.00"
                        value={amountPaid}
                        onChange={(e) => setAmountPaid(e.target.value)}
                        className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary text-lg"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <Button
                      onClick={() => updatePayment(scannedRegistrant.payment_status === "paid" ? "unpaid" : "paid")}
                      className={`h-12 ${
                        scannedRegistrant.payment_status === "paid"
                          ? "bg-[#FF0055] hover:bg-[#FF0055]/90"
                          : "bg-[#CCFF00] hover:bg-[#CCFF00]/90 text-black"
                      }`}
                      data-testid="scanned-toggle-payment"
                    >
                      {scannedRegistrant.payment_status === "paid"
                        ? "Mark Unpaid"
                        : "Mark Paid"}
                    </Button>
                    
                    {!scannedRegistrant.collected ? (
                      <Button
                        onClick={markCollected}
                        className="h-12 bg-primary hover:bg-primary/90 text-black"
                        data-testid="scanned-mark-collected"
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Collected
                      </Button>
                    ) : (
                      <Button
                        disabled
                        className="h-12 bg-muted text-muted-foreground"
                        data-testid="scanned-already-collected"
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Already Collected
                      </Button>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="w-full border-white/10"
                    onClick={() => navigate(`/admin/registrant/${scannedRegistrant.id}`)}
                    data-testid="view-full-details-btn"
                  >
                    View Full Details
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions */}
        {!scannedRegistrant && !scanning && (
          <Card className="glass border-white/10">
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground text-sm">
                Enter a registration number above (like "1" or "001") or start the camera to scan a QR code.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
